import { create } from 'zustand';
import type { AxiosRequestConfig } from 'axios';
import { WILDCARD_EXCLUDED_ACTIONS, type Action } from '@b1fr0st/rbac';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient } from '@/lib/api-client';
import { financeStream } from '@/lib/finance-stream';
import { notificationToastQueue } from '@/lib/toast-queue';
import { queryClient } from '@/lib/query-client';
import { PORTAL_PATH, PORTAL_QUERY_ROOT, type PartnerMe } from '@/pages/partner-portal/portal-api';

/**
 * The actions `<resource>:*` deliberately does not produce
 * (`WILDCARD_EXCLUDED_ACTIONS` in `@b1fr0st/rbac`). Imported rather than
 * retyped: a fourth carved-out action must tighten this console on the day it
 * lands in the catalogue, not whenever somebody notices a copy has drifted.
 */
function isCarvedOut(action: string | undefined): boolean {
  return action !== undefined && WILDCARD_EXCLUDED_ACTIONS.has(action as Action);
}

export interface AuthUser {
  id: string;
  username: string;
  displayName?: string;
  roleLevel?: number;
  /** Role code as the session reports it (e.g. `cs_lead`). The watermark
   *  prefers it over the bare level; absent on a session minted before the
   *  login reply carried one. */
  roleCode?: string;
  email?: string;
  /** Client IP as the GATEWAY derived it (`x-real-ip`), resolved by `/auth/me`
   *  — never asserted by the browser, which cannot know its own public
   *  address. `null` when no edge stamped one (a direct dev hit). */
  ip?: string | null;
  /**
   * Permission codes granted to this staff user (e.g. `members.read`,
   * `wallet.adjust`). Backend may return them on login; an empty/missing
   * array is treated as "no extra permissions" and gated routes fall back
   * to roleLevel-based checks when appropriate.
   */
  permissions?: string[];
}

/**
 * Whether this session is all-access by rung rather than by grant.
 *
 * L6 root and the L90+ super-admin bypass the backend's `rbacGuard` outright
 * (`packages/shared/src/middleware/rbac-guard.ts`), so every permission check in
 * this console has to agree with them or the console offers less than the server
 * allows. `undefined` — a session minted before the login reply carried a level —
 * is deliberately NOT root.
 *
 * It is also the only way the SPA can say "root": L5 `super_admin` already holds
 * `*:*`, so no permission code distinguishes the two rungs.
 */
export function isRootLevel(user: AuthUser | null | undefined): boolean {
  return user?.roleLevel === 6 || (user?.roleLevel ?? 0) >= 90;
}

export type LoginResult = { kind: 'ok' } | { kind: 'mfa-required' };

/** Who the session belongs to: back-office staff on `/api/bo`, or an affiliate partner on `/api/partner`. */
export type SubjectKind = 'admin' | 'partner';

export function audienceFor(kind: SubjectKind): 'bo' | 'partner' {
  return kind === 'partner' ? 'partner' : 'bo';
}

/** A request that must not enter the 401 refresh-and-replay path: a refused login is a wrong password, not an expired session. */
const NO_REFRESH: AxiosRequestConfig & { _retry: boolean } = { _retry: true };

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  subjectKind: SubjectKind;
  login: (
    username: string,
    password: string,
    mfaCode?: string,
    subjectKind?: SubjectKind,
  ) => Promise<LoginResult>;
  logout: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  /** Refetch the current admin's effective permissions from svc-auth and merge
   *  them into `user`, so a role's permission edits apply without a re-login. */
  refreshPermissions: () => Promise<void>;
  hasPermission: (code: string) => boolean;
  /** True if the user may modify `resource` — holds any non-read action on it
   *  (or the resource/global wildcard, or is root/super). */
  canWrite: (resource: string) => boolean;
}

interface LoginResponse {
  accessToken: string;
  /** Absent once REFRESH_TOKEN_IN_BODY is off — the token arrives as a cookie. */
  refreshToken?: string;
  user: AuthUser;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      subjectKind: 'admin',
      async login(username, password, mfaCode, subjectKind = 'admin') {
        set({ isLoading: true });
        try {
          const { data } = await apiClient.post<LoginResponse>(
            `/api/${audienceFor(subjectKind)}/auth/login`,
            { username, password, subjectKind, ...(mfaCode ? { mfaCode } : {}) },
            NO_REFRESH,
          );
          if (subjectKind === 'partner') {
            queryClient.removeQueries({ queryKey: PORTAL_QUERY_ROOT });
            set({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken ?? null,
              user: { id: '', username },
              subjectKind,
              isAuthenticated: true,
              isLoading: false,
            });
            // The partner login reply carries no user. A pending or blocked partner is refused
            // here and still signed in: the portal shell reads the refusal and says why.
            try {
              const me = await apiClient.get<PartnerMe>(`${PORTAL_PATH}/me`);
              if (get().isAuthenticated) set({ user: { id: me.data.id, username: me.data.username } });
            } catch {
              /* the shell asks again */
            }
            return { kind: 'ok' };
          }
          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? null,
            user: data.user,
            subjectKind,
            isAuthenticated: true,
            isLoading: false,
          });
          // The login reply names neither the effective permission set nor the
          // client address, and the boot-time probe in App has already run by
          // the time anyone signs in — without this the shell spends the whole
          // session on the login snapshot, and the page watermark carries no
          // IP until the operator reloads. Fire-and-forget: it is best-effort
          // inside, and a failed probe must not fail the sign-in.
          void get().refreshPermissions();
          return { kind: 'ok' };
        } catch (err) {
          set({ isLoading: false });
          const e = err as { status?: number; raw?: { code?: string } };
          if (e?.status === 401 && e?.raw?.code === 'AUTH-MFA-REQUIRED') {
            return { kind: 'mfa-required' };
          }
          throw err;
        }
      },
      async logout() {
        const { refreshToken, subjectKind } = get();
        try {
          // Always call through: in cookie mode there is no stored token and
          // the server needs the request to expire the cookie server-side.
          await apiClient.post(
            `/api/${audienceFor(subjectKind)}/auth/logout`,
            refreshToken ? { refreshToken } : {},
          );
        } catch {
          /* best-effort */
        }
        financeStream.stop();
        notificationToastQueue.reset();
        if (subjectKind === 'partner') queryClient.removeQueries({ queryKey: PORTAL_QUERY_ROOT });
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken, isAuthenticated: true }),
      setUser: (user) => set({ user }),
      async refreshPermissions() {
        const { isAuthenticated, user, subjectKind } = get();
        if (!isAuthenticated || !user || subjectKind === 'partner') return;
        try {
          const { data } = await apiClient.get<{
            permissions: string[];
            roleLevel: number | null;
            roleCode?: string | null;
            ip?: string | null;
          }>('/api/bo/auth/me');
          // Re-read AFTER the await. Login fires this without waiting for it, so
          // an operator who signs out while the probe is in flight would
          // otherwise have a user object written back over the cleared one —
          // `isAuthenticated: false` with a populated `user`, which the shell
          // reads as a half-session and the persisted storage keeps.
          const current = get();
          if (!current.isAuthenticated || !current.user) return;
          set({
            user: {
              ...current.user,
              permissions: data.permissions,
              roleLevel: data.roleLevel ?? current.user.roleLevel,
              // Merged, not defaulted to the previous value: what the watermark
              // prints has to be what this probe just saw, and a svc-auth that
              // stopped reporting one must blank the stamp rather than keep
              // showing yesterday's network or a role the operator has lost.
              roleCode: data.roleCode ?? undefined,
              ip: data.ip ?? null,
            },
          });
        } catch {
          // Best-effort — keep the login snapshot if the probe fails (offline,
          // token mid-refresh). Enforcement is server-side regardless.
        }
      },
      hasPermission: (code) => {
        const { user, subjectKind } = get();
        if (!user || subjectKind === 'partner') return false;
        if (isRootLevel(user)) return true;
        const perms = user.permissions ?? [];
        if (perms.includes('*') || perms.includes('*:*') || perms.includes('*.*')) return true;
        // Exact match, tolerant of dot/colon separator drift (canonical codes are colon).
        if (perms.includes(code) || perms.includes(code.replace(/\./g, ':')) || perms.includes(code.replace(/:/g, '.'))) {
          return true;
        }
        // Wildcard prefix match — supports both dot and colon forms, and honours
        // the SAME carve-out the catalogue does. `member:*` must not offer the
        // reveal button, the password reset or login-as-player: the server
        // refuses all three, and a console that offers an action the server
        // refuses teaches an operator that permissions are unreliable — which
        // gets answered by widening a role by hand.
        const [resource, action] = code.split(/[.:]/);
        if (isCarvedOut(action)) return false;
        return perms.includes(`${resource}.*`) || perms.includes(`${resource}:*`);
      },
      canWrite: (resource) => {
        const { user, subjectKind } = get();
        if (!user || subjectKind === 'partner') return false;
        if (isRootLevel(user)) return true;
        const perms = user.permissions ?? [];
        if (perms.includes('*') || perms.includes('*:*') || perms.includes('*.*')) return true;
        if (perms.includes(`${resource}:*`) || perms.includes(`${resource}.*`)) return true;
        // Any non-read action on the resource grants write access to its section.
        // Read-like actions ('read', 'node_read', 'credit_read') never do — and
        // neither does a carved-out one: `member:reveal_pii` is a read of a
        // single field and `member:impersonate` opens a read-only player view,
        // so holding either must not light up the section's edit controls.
        return perms.some((p) => {
          const [r, action] = p.split(/[.:]/);
          if (!action || r !== resource) return false;
          if (action === 'read' || action.endsWith('_read')) return false;
          return !isCarvedOut(action);
        });
      },
    }),
    {
      name: 'b1fr0st.auth',
      // Impersonation tabs use sessionStorage (per-tab, set by
      // impersonation-bootstrap before this store initializes) so a "login as
      // child node" session never clobbers the parent admin's localStorage
      // session, which is shared across all tabs.
      storage: createJSONStorage(() =>
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('b1fr0st.impersonation') === '1'
          ? sessionStorage
          : localStorage,
      ),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        isAuthenticated: s.isAuthenticated,
        subjectKind: s.subjectKind,
      }),
    },
  ),
);
